import { useEffect, useState } from 'react';
import OverlayLoader from '../../../../../components/overlayLoader';
import LoanCardList from '../components/LoanCardList';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../services/auth/AuthContext';

import fetchApplicationsService from '../../../../../services/api/fetchApplications';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function SentToField({ loanType }) {
	const { t } = useTranslation();

	const { setIsAuthenticated } = useAuth();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	const source = searchParams.get('source') ?? 'all';
	const campaign = searchParams.get('campaign') ?? 'all';
	const state = searchParams.get('state') ?? 'all';
	const priority = searchParams.get('priority') ?? 'all';
	const dispositions = searchParams.get('dispositions') ?? 'all';
	const sort = searchParams.get('sort') ?? 'default';
	const rejectionReason = searchParams.get('rejectionReason') ?? 'all';
	const searchBy = searchParams.get('search_by') ?? 'phone';
	const searchTerm = searchParams.get('search_term') ?? '';
	const [applications, setApplications] = useState([]);
	const [resellerData, setResellerData] = useState({});
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [cursor, setCursor] = useState(null);

	const fetchLoanApplicationsData = async (isNewSearch = false, isLoading = true) => {
		try {
			setLoading(isLoading);

			// Check if this is a fresh search or if the flag is explicitly set
			const isInitialLoad = isNewSearch || applications.length === 0;

			// The cursor is the backend's, not ours: under a sort other than
			// newest-id-first, the last row's id alone does not identify the page
			// boundary, so the pair it returns has to come back verbatim.
			const pageCursor = isNewSearch ? null : cursor;

			const response = await fetchApplicationsService.getApplicationsForStatusCode(
				null,
				{
					source: source,
					campaign: campaign,
					state: state,
					priority: priority,
					dispositions: dispositions,
					sort: sort,
					...(searchTerm && { [searchBy]: searchTerm }),
					...(pageCursor && {
						last_id: pageCursor.last_id,
						last_sort_value: pageCursor.last_sort_value ?? '',
					}),
					limit: 5,
					rejection_reason: rejectionReason,
					current_application_status: 'pushed_to_field',
					loan_type_code: loanType,
				},
				setIsAuthenticated,
				navigate
			);

			if (isInitialLoad) {
				// If it's a new search, replace the applications
				setApplications(response.applications || []);
			} else {
				// If it's pagination, append the new applications
				setApplications(prevApplications => [...prevApplications, ...(response.applications || [])]);
			}

			setResellerData((prev) => ({
				...prev,
				...response.reseller_data,
			}));
			setHasMore(response.has_more);
			setCursor(response.next_cursor ?? null);
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		setApplications([]);
		setCursor(null);
		setHasMore(true);
		fetchLoanApplicationsData(true);
	}, [source, campaign, state, priority, dispositions, rejectionReason, sort, searchBy, searchTerm]);

	return (
		<Box
			display={'flex'}
			flexDirection={'column'}
			height={'100%'}
			overflow={'auto'}
		>
			<OverlayLoader
				open={loading}
				text={t('loading.in_progress') + '...'}
			/>

			<LoanCardList
				applications={applications}
				setApplications={setApplications}
				resellerData={resellerData}
				loanType={loanType}
				loadMore={() => fetchLoanApplicationsData(false, false)}
				hasMore={hasMore}
				displayDispositionFilter={false}
				showAllCloudDownload
			/>
		</Box>
	);
}
